import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import type {
  DomainEntity,
  EntityDetail,
  EntityDetailSchema,
  EnvironmentKey,
} from '@web-config-app/core';
import { isUsingConfigAppBackend } from '@web-config-app/common';
import {
  getAllFeatureFlags,
  getEntityInstanceName,
  ruleTemplatesEditorStateStore,
  aiAssistPromptsEditorStateStore,
} from '@web-config-app/core';
import {
  useConfigEntity,
  useEntityOperations,
  useEntityData,
  useEntityMetadata,
  useSetEntityData,
  useSetEntityMeta,
  useEntityMeta,
  useResetEntityData,
  useIncludedEntities,
  useAddIncludedEntity,
  useSetIncludedEntities,
  parseEditorStateEntityMetadata,
  useUpdateEditorStateInEntityMetadata,
  useRuleTemplatesEditorStateStoreActions,
  useAiAssistPromptsEditorStateStoreActions,
} from '@web-config-app/core-react';
import type {
  UseEntityOperationsProps,
  UseMultiEnvironmentEntityOperationsProps,
  BulkActionReportingStore,
  OtherAuthoringEnvironmentCandidate,
} from '@web-config-app/core-react';
import {
  EntityDetailsProvider,
  createDefaultValueForSchema,
  createDefaultValueForConfigEntity,
  useSyncDataSources,
  useDataSourceStore,
} from '@web-config-app/entity-form';
import {
  applyDefaultSchemaAnnotations,
  computeSchema,
  applyConditionalLogic,
} from '@web-config-app/schema-utils';
import { EntityDetailsMultiEnvironmentContainer } from '../entity-details-multi-environment-container/entity-details-multi-environment.container';

export interface EntityDetailsPageContainerProps {
  domainEntity: DomainEntity;
  // TODO:: Fix inconsistent typing of instanceId since it can be undefined
  // https://everlong.atlassian.net/browse/CACT-1600
  instanceId: string;
  currentAuthoringEnvironment: EnvironmentKey;
  path: string;
  options?: UseEntityOperationsProps['options'];
  multiEnvironmentOptions?: UseMultiEnvironmentEntityOperationsProps['options'];
  createDefaultEntityData?: boolean;
  /**
   * Every valid promotion target for this tenant. Empty when the tenant has no preview
   * environment at all (single-environment mode) -- gates whether the multi-environment
   * container renders at all, same as the old singular otherAuthoringEnvironment did.
   */
  otherAuthoringEnvironments: OtherAuthoringEnvironmentCandidate[];
  tenantId: string;
  /**
   * Optional external bulk action reporting store shared with mutation
   * callbacks that drive the clone/deepClone reporting modal.
   */
  bulkActionReportingStore: BulkActionReportingStore;
}

export const EntityDetailsPageContainer = ({
  domainEntity,
  instanceId,
  currentAuthoringEnvironment,
  options,
  multiEnvironmentOptions,
  path,
  children,
  createDefaultEntityData,
  otherAuthoringEnvironments,
  tenantId,
  bulkActionReportingStore,
}: React.PropsWithChildren<EntityDetailsPageContainerProps>) => {
  const { formatMessage } = useIntl();
  const configEntity = useConfigEntity({ domainEntity });

  const isConfigAppBackendEnabled = isUsingConfigAppBackend({
    entityLevelOverride: domainEntity.isUsingConfigAppBackend,
  });

  /**
   * The base schema is the schema as it is stored in the entity config without
   * any conditional logic applied.
   */
  const baseSchema = React.useMemo(
    () => applyDefaultSchemaAnnotations(domainEntity.schema),
    [domainEntity.schema],
  );

  const entityConfigName = formatMessage(
    { id: domainEntity.labelKey },
    { count: 1 },
  );

  const defaultEntityName = React.useMemo(
    () => `${formatMessage({ id: 'UNTITLED' })} ${entityConfigName}`,
    [formatMessage, entityConfigName],
  );
  const defaultEntityData = React.useMemo(() => {
    if (!createDefaultEntityData) {
      return undefined;
    }

    return isConfigAppBackendEnabled
      ? createDefaultValueForConfigEntity(
          configEntity.schema,
          domainEntity.schema,
          defaultEntityName,
        )
      : createDefaultValueForSchema<EntityDetail>(baseSchema);
  }, [
    createDefaultEntityData,
    isConfigAppBackendEnabled,
    configEntity.schema,
    domainEntity.schema,
    defaultEntityName,
    baseSchema,
  ]);

  const dataSourceStore = useDataSourceStore();
  const entityRootData = useEntityData();
  const entityMeta = useEntityMeta();
  const setEntityData = useSetEntityData();
  const setEntityMeta = useSetEntityMeta();
  const resetEntityData = useResetEntityData();
  const includedEntities = useIncludedEntities();
  const setIncludedEntities = useSetIncludedEntities();
  const addIncludedEntity = useAddIncludedEntity();
  const { setRuleTemplatesData, resetRuleTemplatesData } =
    useRuleTemplatesEditorStateStoreActions();
  const { setAiAssistPromptsData, resetAiAssistPromptsData } =
    useAiAssistPromptsEditorStateStoreActions();
  const { status } = useEntityMetadata();
  const updateEditorStateInEntityMetadata =
    useUpdateEditorStateInEntityMetadata();

  const entityRootDataRef = React.useRef(entityRootData);
  entityRootDataRef.current = entityRootData;

  const updateEditorStateRef = React.useRef(updateEditorStateInEntityMetadata);
  updateEditorStateRef.current = updateEditorStateInEntityMetadata;

  /**
   * Subscribes to changes in the ruleTemplatesEditorStateStore and updates the entity metadata with the latest editor state.
   */
  React.useEffect(() => {
    // TODO:: ensure this only writes to ruleTemplates in editorState https://everlong.atlassian.net/browse/CACT-2061
    const unsubscribe = ruleTemplatesEditorStateStore.subscribe((state) => {
      const ruleTemplatesEditorData = state.ruleTemplatesData;
      if (!ruleTemplatesEditorData) return;
      // we need to check if the entityRootData is defined before
      // updating the editor state, otherwise we supply kind of a
      // weird default value that makes it impossible to tell
      // when the actual data is loaded.
      if (entityRootDataRef.current) {
        updateEditorStateRef.current({
          ruleTemplates: ruleTemplatesEditorData,
          aiAssistPrompts:
            aiAssistPromptsEditorStateStore.getState().aiAssistPromptsData,
        });
      }
    });
    return unsubscribe;
  }, []);

  /**
   * Subscribes to changes in the aiAssistPromptsEditorStateStore and updates the entity metadata with the latest editor state.
   */
  React.useEffect(() => {
    const unsubscribe = aiAssistPromptsEditorStateStore.subscribe((state) => {
      const aiAssistPromptsEditorData = state.aiAssistPromptsData;
      if (!aiAssistPromptsEditorData) return;
      // we need to check if the entityRootData is defined before
      // updating the editor state, otherwise we supply kind of a
      // weird default value that makes it impossible to tell
      // when the actual data is loaded.
      if (entityRootDataRef.current) {
        updateEditorStateRef.current({
          ruleTemplates:
            ruleTemplatesEditorStateStore.getState().ruleTemplatesData,
          aiAssistPrompts: aiAssistPromptsEditorData,
        });
      }
    });
    return unsubscribe;
  }, []);

  /**
   * Reset rule template and ai assist prompts editor state on unmount of page
   */
  React.useEffect(
    () => () => {
      resetRuleTemplatesData();
      resetAiAssistPromptsData();
    },
    [resetRuleTemplatesData, resetAiAssistPromptsData],
  );

  /**
   * Reset the data source store on mount
   */
  React.useEffect(() => {
    dataSourceStore.getState().reset();
  }, [dataSourceStore]);

  /**
   * The name of the entity is either stored in entityMetadata or is located
   * in some custom location resulting in extra work being done inside
   * `getEntityInstanceName`.
   *
   * TODO: Refactor in https://everlong.atlassian.net/browse/CACT-1875
   */
  const name = getEntityInstanceName(entityRootData, domainEntity);
  /**
   * We apply conditional rendering to the baseSchema using the current
   * entity data which will return either the full baseSchema or a schema
   * with some properties removed in the event that the conditions for
   * including them are not met
   */

  const featureFlags = getAllFeatureFlags();
  const featureFlagsKey = JSON.stringify(featureFlags);

  const rootSchema = React.useMemo(() => {
    const rootEntityAttributes = isConfigAppBackendEnabled
      ? entityRootData?.attributes?.domainEntityAttributes
      : entityRootData?.attributes;

    const computedAttributesSchema = computeSchema(
      baseSchema.properties.attributes,
      rootEntityAttributes,
      [applyConditionalLogic],
      {
        recursive: false,
        // Provide rootData for `$` prefixed paths in conditionals
        rootData: rootEntityAttributes,
        featureFlags,
      },
    );

    return {
      ...baseSchema,
      properties: {
        ...baseSchema.properties,
        attributes: computedAttributesSchema,
      },
    } as EntityDetailSchema;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    baseSchema,
    entityRootData?.attributes,
    isConfigAppBackendEnabled,
    featureFlagsKey,
  ]);

  useSyncDataSources(
    rootSchema,
    entityRootData ?? {},
    includedEntities,
    domainEntity,
  );

  const operationsEntity =
    domainEntity.isUsingConfigAppBackend === false
      ? domainEntity
      : configEntity;

  const operations = useEntityOperations({
    entity: operationsEntity,
    instanceId,
    options,
    isUsingConfigAppBackend: isConfigAppBackendEnabled,
    entityRootData: entityRootData ?? {},
    bulkActionReportingStore,
    onEntityGetSuccess: (data, includedEntitiesData, meta) => {
      setEntityData(data);
      setIncludedEntities(includedEntitiesData);
      setEntityMeta(meta);

      const editorStateEntityMetadata = parseEditorStateEntityMetadata(data);
      if (editorStateEntityMetadata?.ruleTemplates) {
        setRuleTemplatesData(editorStateEntityMetadata.ruleTemplates);
      }
      if (editorStateEntityMetadata?.aiAssistPrompts) {
        setAiAssistPromptsData(editorStateEntityMetadata.aiAssistPrompts);
      }
    },
    environment: currentAuthoringEnvironment,
  });

  React.useEffect(() => {
    if (createDefaultEntityData && defaultEntityData) {
      setEntityData(defaultEntityData);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  return otherAuthoringEnvironments.length > 0 ? (
    <EntityDetailsMultiEnvironmentContainer
      name={name}
      status={status}
      domainEntity={domainEntity}
      defaultEntityName={defaultEntityName}
      instanceId={instanceId}
      multiEnvironmentOptions={multiEnvironmentOptions}
      entityRootData={entityRootData}
      entityMeta={entityMeta}
      setEntityData={setEntityData}
      currentAuthoringEnvironment={currentAuthoringEnvironment}
      otherAuthoringEnvironments={otherAuthoringEnvironments}
      operations={operations}
      rootSchema={rootSchema}
      path={path}
      includedEntities={includedEntities}
      setIncludedEntities={setIncludedEntities}
      addIncludedEntity={addIncludedEntity}
      resetEntityData={resetEntityData}
      isUsingConfigAppBackend={isConfigAppBackendEnabled}
      tenantId={tenantId}
      bulkActionReportingStore={bulkActionReportingStore}
    >
      {children}
    </EntityDetailsMultiEnvironmentContainer>
  ) : (
    <EntityDetailsProvider
      name={name}
      status={status}
      defaultEntityName={defaultEntityName}
      entityRootData={entityRootData}
      entityMeta={entityMeta}
      setEntityData={setEntityData}
      currentAuthoringEnvironment={currentAuthoringEnvironment}
      operations={operations}
      rootSchema={rootSchema}
      path={path}
      instanceId={instanceId}
      entityType={domainEntity.type}
      domainEntity={domainEntity}
      configEntitySchema={configEntity.schema}
      includedEntities={includedEntities}
      setIncludedEntities={setIncludedEntities}
      addIncludedEntity={addIncludedEntity}
      resetEntityData={resetEntityData}
      isUsingConfigAppBackend={isConfigAppBackendEnabled}
    >
      {children}
    </EntityDetailsProvider>
  );
};
